import { CSSResultGroup, TemplateResult, html } from 'lit';
import {
  customElement,
  property,
  query,
  queryAssignedElements,
  state,
} from 'lit/decorators.js';
import { OutlineElement } from 'src/components/base/outline-element/outline-element';
import componentStyles from './idb-horizontal-scroll.css.lit';
import { classMap } from 'lit/directives/class-map.js';

@customElement('idb-horizontal-scroll')
export class IdbHorizontalScroll extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, attribute: 'max-width' })
  maxWidth: string;

  @query('.horizontal-scroll__container')
  container: HTMLElement;

  @queryAssignedElements()
  slotted: Array<HTMLElement>;

  @property({ type: String, attribute: 'left-arrow-text' })
  leftArrowText: string;

  @property({ type: String, attribute: 'right-arrow-text' })
  rightArrowText: string;

  @state() hasScroll = false;

  updateArrows() {
    requestAnimationFrame(() => {
      const slottedWidth = this.slotted[0].offsetWidth;
      if (slottedWidth > this.container.offsetWidth) {
        this.hasScroll = true;
      } else {
        this.hasScroll = false;
      }
    });
  }

  firstUpdated() {
    this.updateArrows();
    new ResizeObserver(() => {
      this.updateArrows();
    }).observe(document.querySelector('body') as Element);
  }

  leftScroll() {
    this.container.scrollBy({
      left: -200,
      behavior: 'smooth',
    });
  }

  rightScroll() {
    this.container.scrollBy({
      left: 200,
      behavior: 'smooth',
    });
  }

  render(): TemplateResult {
    const leftButtonClasses = {
      'arrow-left': true,
      'disabled': !this.hasScroll,
    };

    const rightButtonClasses = {
      'arrow-right': true,
      'disabled': !this.hasScroll,
    };

    return html`
      <div class="horizontal-scroll">
        <button class="${classMap(leftButtonClasses)}" 
          @click=${this.leftScroll}
          ?disabled="${!this.hasScroll}"
          aria-label="${this.leftArrowText}">
          <outline-icon 
            name="chevron-left" 
            library="bootstrap" 
            size="24px">
          </outline-icon>
        </button>
        <div 
          class="horizontal-scroll__container" 
          style=${`max-width: ${this.maxWidth}`}>
          <span class="extra-space"></span>
          <slot></slot>
          <span class="extra-space"></span>
        </div>
        <button class="${classMap(rightButtonClasses)}" 
          @click=${this.rightScroll}
          ?disabled="${!this.hasScroll}"
          aria-label="${this.rightArrowText}"">
          <outline-icon 
            name="chevron-right" 
            library="bootstrap" 
            size="24px">
          </outline-icon>
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'idb-horizontal-scroll': IdbHorizontalScroll;
  }
}
